import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  CapacityManagementRootResponse,
  ReportingBalancingGroupAllocationsParams,
  ReportingBalancingGroupAllocationsResponse,
} from 'src/apis/capacity-management/types';
import type { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params?: ReportingBalancingGroupAllocationsParams;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params?: ReportingBalancingGroupAllocationsParams;
};

export function useBalancingGroupAllocations({
  capacityManagementRoot,
  params,
}: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { rootData, params }: RequestOptions) => {
      if (!rootData._links?.balancingGroupAllocations) throw new Forbidden();

      const url = urlTemplate
        .parse(rootData._links.balancingGroupAllocations.href)
        .expand({});
      return axios.request<ReportingBalancingGroupAllocationsResponse>({
        ...baseConfig,
        url,
        params,
      });
    },
    {
      neededOnPageLoad: true,
    }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, params };

    request.execute(options);
    // oxlint-disable-next-line exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return request;
}

export type BalancingGroupAllocationsRequest = ReturnType<
  typeof useBalancingGroupAllocations
>;
